import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  Stack,
} from '@mui/material';
import { styled } from '@mui/system';
import InstagramIcon from '@mui/icons-material/Instagram';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const cardBase = {
  purple: {
    accent: '#D484FF',
    accentStrong: '#9D00FF',
    accentSoft: 'rgba(210,110,255,0.18)',
    buttonText: 'rgba(32, 12, 52, 0.96)',
    buttonGradient:
      'linear-gradient(180deg, rgba(240,182,255,1) 0%, rgba(212,132,255,1) 45%, rgba(180,96,245,1) 100%)',
    glow: '0 0 34px rgba(212,132,255,0.16)',
  },
  green: {
    accent: '#AAE562',
    accentStrong: '#72A62E',
    accentSoft: 'rgba(170,229,98,0.14)',
    buttonText: 'rgba(20, 34, 10, 0.96)',
    buttonGradient:
      'linear-gradient(180deg, #aae562 0%, #72a62e 42%, #5b7c2e 100%)',
    glow: '0 0 34px rgba(140,255,0,0.14)',
  },
};

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'accentMode',
})(({ theme, accentMode = 'purple' }) => {
  const colors = cardBase[accentMode];

  return {
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
    overflow: 'hidden',
    minHeight: 220,
    height: '100%',
    borderRadius: theme.spacing(2.4),
    background:
      'linear-gradient(145deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.025) 48%, rgba(0,0,0,0.22) 100%)',
    border: `1px solid ${colors.accentSoft}`,
    boxShadow:
      `0 18px 38px rgba(0,0,0,0.42), ${colors.glow}, inset 0 1px 0 rgba(255,255,255,0.08)`,
    isolation: 'isolate',

    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      background:
        `radial-gradient(circle at 18% 0%, ${colors.accentSoft}, transparent 34%)`,
      opacity: 0.9,
      zIndex: 0,
    },

    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 3,
      background: `linear-gradient(90deg, transparent, ${colors.accent}, transparent)`,
      opacity: 0.8,
      zIndex: 2,
    },

    [theme.breakpoints.down('sm')]: {
      minHeight: 200,
    },
  };
});

const ImageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'side',
})(({ theme, side = 'left' }) => ({
  width: '50%',
  position: 'relative',
  display: 'flex',
  alignItems: 'stretch',
  justifyContent: side === 'left' ? 'flex-start' : 'flex-end',
  overflow: 'hidden',
  zIndex: 1,

  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    filter: 'grayscale(1) contrast(1.1) brightness(0.92)',
    transform: 'scale(1.02)',
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    background:
      side === 'left'
        ? 'linear-gradient(90deg, transparent 0%, transparent 68%, rgba(0,0,0,0.30) 100%)'
        : 'linear-gradient(270deg, transparent 0%, transparent 68%, rgba(0,0,0,0.30) 100%)',
  },

  [theme.breakpoints.down('sm')]: {
    width: '50%',
  },
}));

const ContentContainer = styled(CardContent, {
  shouldForwardProp: (prop) => prop !== 'alignSide',
})(({ theme, alignSide = 'right' }) => ({
  width: '50%',
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: alignSide === 'left' ? 'flex-start' : 'center',
  textAlign: alignSide === 'left' ? 'left' : 'center',
  padding: theme.spacing(3),

  [theme.breakpoints.down('sm')]: {
    width: '50%',
    padding: theme.spacing(2),
  },
}));

const FollowButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'accentMode',
})(({ theme, accentMode = 'purple' }) => ({
  width: '100%',
  maxWidth: 280,
  marginTop: theme.spacing(2),
  minHeight: 56,
  borderRadius: '999px',
  padding: theme.spacing(1.25, 3),
  color: 'rgba(32, 12, 52, 0.96)',
  position: 'relative',
  overflow: 'hidden',
  fontWeight: 900,
  fontSize: '1rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  background:
    'linear-gradient(180deg, rgba(240,182,255,1) 0%, rgba(212,132,255,1) 45%, rgba(180,96,245,1) 100%)',
  boxShadow: `
    0 14px 30px rgba(160,90,235,0.32),
    0 0 28px rgba(212,132,255,0.14),
    inset 0 1px 0 rgba(255,255,255,0.32)
  `,
  border: '1px solid rgba(255,255,255,0.18)',
  transition: 'transform 120ms ease, filter 160ms ease, box-shadow 160ms ease',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background:
      'linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.04) 38%, rgba(255,255,255,0) 100%)',
    pointerEvents: 'none',
  },

  '& svg': {
    color: 'rgba(32, 12, 52, 0.96)',
    position: 'relative',
    zIndex: 1,
  },

  '& .MuiButton-startIcon, & .MuiButton-endIcon': {
    position: 'relative',
    zIndex: 1,
  },

  '&:hover': {
    background:
      'linear-gradient(180deg, rgba(245,195,255,1) 0%, rgba(218,145,255,1) 45%, rgba(185,104,250,1) 100%)',
    boxShadow: `
      0 16px 34px rgba(160,90,235,0.38),
      0 0 34px rgba(212,132,255,0.18),
      inset 0 1px 0 rgba(255,255,255,0.36)
    `,
  },

  '&:active': {
    transform: 'scale(0.985)',
    filter: 'brightness(0.96)',
  },

  [theme.breakpoints.down('sm')]: {
    minHeight: 48,
    fontSize: '0.86rem',
    padding: theme.spacing(1.05, 2),
  },
}));

const MicroLabel = styled(Typography)(({ theme }) => ({
  opacity: 0.68,
  letterSpacing: '0.17em',
  textTransform: 'uppercase',
  fontSize: '0.72rem',
  marginBottom: theme.spacing(0.8),

  [theme.breakpoints.down('sm')]: {
    fontSize: '0.58rem',
    letterSpacing: '0.13em',
  },
}));

const ComicName = styled(Typography)(({ theme }) => ({
  fontWeight: 900,
  letterSpacing: '-0.035em',
  lineHeight: 1,
  textShadow: '0 4px 18px rgba(0,0,0,0.45)',

  [theme.breakpoints.down('sm')]: {
    fontSize: '1.55rem',
  },
}));

const HandleText = styled(Typography)(({ theme }) => ({
  opacity: 0.78,
  fontWeight: 700,
  fontSize: '0.95rem',

  [theme.breakpoints.down('sm')]: {
    fontSize: '0.78rem',
  },
}));

const InstagramFollowCard = ({
  name,
  handle,
  href,
  imageSrc,
  imageAlt,
  accentMode = 'purple',
  imageSide = 'left',
}) => {
  const image = (
    <ImageContainer side={imageSide}>
      <img src={imageSrc} alt={imageAlt} />
    </ImageContainer>
  );

  const content = (
    <ContentContainer alignSide={imageSide === 'left' ? 'right' : 'left'}>
      <Stack
        spacing={0.5}
        alignItems={imageSide === 'left' ? 'center' : 'flex-start'}
        sx={{ width: '100%' }}
      >
        <MicroLabel>
          Follow for good luck
        </MicroLabel>

        <ComicName variant="h4">
          {name}
        </ComicName>

        <HandleText>
          {handle}
        </HandleText>

        <FollowButton
          accentMode={accentMode}
          startIcon={<InstagramIcon />}
          endIcon={<ArrowForwardIcon />}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
        >
          Follow
        </FollowButton>
      </Stack>
    </ContentContainer>
  );

  return (
    <StyledCard accentMode={accentMode}>
      {imageSide === 'left' ? (
        <>
          {image}
          {content}
        </>
      ) : (
        <>
          {content}
          {image}
        </>
      )}
    </StyledCard>
  );
};

const InstagramAd = () => {
  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        variant="h3"
        align="center"
        sx={{
          mb: 2.5,
          fontWeight: 900,
          letterSpacing: '-0.045em',
          textShadow: '0 5px 24px rgba(0,0,0,0.55)',
          fontSize: { xs: '2.5rem', sm: '3.2rem', md: '3.8rem' },
        }}
      >
        We should hangout sometime
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <InstagramFollowCard
            name="David"
            //handle="@gods_father"
            href="https://www.instagram.com/gods_father"
            imageSrc={`${process.env.PUBLIC_URL}/assets/DavidMay_ripped.png`}
            imageAlt="David May"
            accentMode="purple"
            imageSide="left"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <InstagramFollowCard
            name="Nathaniel"
            //handle="@nathaniel.c.webb"
            href="https://www.instagram.com/nathaniel.c.webb"
            imageSrc={`${process.env.PUBLIC_URL}/assets/Nathaniel_Ripped.png`}
            imageAlt="Nathaniel Webb"
            accentMode="green"
            imageSide="right"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default InstagramAd;


